import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { IFieldEditor } from "../../../../models/Interfaces";

export const parseCollectionItems = <T,>(value?: string | null): T[] => {
  try { const parsed: unknown = JSON.parse(value || "[]"); return Array.isArray(parsed) ? parsed : []; }
  catch { return []; }
};

// Hydration never emits an empty collection back to the component. All four
// item editors use this setter so only actual user actions create drafts.
export function useCollectionItems<T>(props: IFieldEditor): [T[], Dispatch<SetStateAction<T[]>>] {
  const value = props.property.PublishedValue ?? props.property.DefaultValue;
  const [items, setState] = useState<T[]>(() => parseCollectionItems<T>(value));
  const itemsRef = useRef(items);
  useEffect(() => {
    const next = parseCollectionItems<T>(value);
    itemsRef.current = next;
    setState(next);
  }, [value]);
  const setItems: Dispatch<SetStateAction<T[]>> = update => {
    const next = typeof update === "function" ? update(itemsRef.current) : update;
    itemsRef.current = next;
    setState(next);
    props.onValueChange(props.property.Id, JSON.stringify(next));
  };
  return [items, setItems];
}

export const moveCollectionItem = <T,>(items: T[], index: number, offset: number): T[] => {
  const target = index + offset;
  if (!items[index] || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export const duplicateCollectionItem = <T extends { Id: number | string }>(items: T[], index: number): T[] => {
  const item = items[index];
  if (!item) return items;
  const id = typeof item.Id === "string" ? crypto.randomUUID()
    : nextCollectionNumber(items);
  return [...items.slice(0, index + 1), { ...item, Id: id }, ...items.slice(index + 1)];
};

export const nextCollectionNumber = (items: { Id: number | string }[]): number =>
  Math.max(0, ...items.map(item => typeof item.Id === "number" ? item.Id : 0)) + 1;

export const copyCollectionItems = <T extends { Id: number | string }>(items: T[]): T[] => {
  const nextId = nextCollectionNumber(items);
  return items.map((item, index) => ({ ...item, Id: typeof item.Id === "string" ? crypto.randomUUID() : nextId + index }));
};
